import { useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  FileText, UserCheck, ShieldAlert, CreditCard, Star,
  Globe, Lock, AlertCircle, Scale, RefreshCw, Mail, ArrowRight, Sparkles, Ban, Gavel,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

/* ── Scroll reveal ── */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const sections = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "definitions", label: "Definitions" },
  { id: "eligibility", label: "Eligibility" },
  { id: "accounts", label: "Accounts & Registration" },
  { id: "platform-role", label: "Platform Role" },
  { id: "student-obligations", label: "Student Obligations" },
  { id: "coach-obligations", label: "Teacher Obligations" },
  { id: "payments", label: "Payments & Fees" },
  { id: "cancellations", label: "Cancellations & Refunds" },
  { id: "content", label: "User Content" },
  { id: "prohibited", label: "Prohibited Conduct" },
  { id: "ip", label: "Intellectual Property" },
  { id: "third-party", label: "Third-Party Services" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "termination", label: "Termination" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact Us" },
];

const highlights = [
  { icon: Scale, title: "Fair marketplace rules", desc: "Clear obligations for both students and teachers. No hidden clauses." },
  { icon: CreditCard, title: "Transparent fees", desc: "Platform fees and payout schedules are clearly disclosed before you transact." },
  { icon: ShieldAlert, title: "We are a marketplace", desc: "BookMyCoaching facilitates, but does not provide, teaching services directly." },
  { icon: Gavel, title: "Dispute resolution", desc: "We offer mediation support for disputes between students and teachers." },
];

export default function TermsPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const { pathname } = useLocation();

  const title = 'Terms of Service | BookMyCoaching';
  const description = "Read BookMyCoaching's terms of service.";
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="relative pt-28 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(230,55%,15%)] via-[hsl(210,55%,18%)] to-[hsl(190,45%,14%)]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[150px]" />
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-[hsl(260,60%,50%)]/8 rounded-full blur-[120px]" />
          </div>

          <div ref={heroRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-5 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20">
              <Sparkles className="w-3 h-3" />
              Legal
            </span>
            <h1 className="font-sans text-4xl sm:text-5xl text-white mb-5 leading-tight">
              Terms of Service
            </h1>
            <p className="text-white/60 max-w-xl mx-auto leading-relaxed mb-4">
              Please read these terms carefully before using BookMyCoaching. They govern your relationship with us and set out your rights and obligations as a user of the platform.
            </p>
            <p className="text-white/35 text-sm">
              Last updated: <span className="text-white/55 font-medium">1 April 2025</span>
              &nbsp;·&nbsp; Effective: <span className="text-white/55 font-medium">1 April 2025</span>
            </p>
          </div>
        </section>

        {/* ── Highlights ── */}
        <section className="py-14 bg-muted/30 border-b border-border/40">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {highlights.map((h, i) => (
                <div key={i} className="flex gap-4 items-start p-5 rounded-2xl bg-card border border-border/40">
                  <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <h.icon className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">{h.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Main content ── */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto flex gap-12">

              {/* Sticky sidebar */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Contents</p>
                  <nav className="flex flex-col gap-1">
                    {sections.map((s) => (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="text-sm text-muted-foreground hover:text-secondary transition-colors py-1 pl-3 border-l-2 border-transparent hover:border-secondary"
                      >
                        {s.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Body */}
              <article className="flex-1 min-w-0">

                {/* Intro banner */}
                <div className="mb-10 p-6 rounded-2xl bg-secondary/5 border border-secondary/20 flex gap-4">
                  <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">Agreement summary</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      These Terms of Service ("Terms") constitute a legally binding agreement between you and <span className="font-medium text-foreground">BookMyCoaching Pvt. Ltd.</span> ("BookMyCoaching", "we", "us", "our"). By creating an account or using any part of the platform, you accept these Terms in full. If you do not agree, do not use the platform.
                    </p>
                  </div>
                </div>

                {/* 1. Acceptance */}
                <div id="acceptance" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={FileText} number="1" title="Acceptance of Terms" />
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    By accessing or using BookMyCoaching — including through our website, mobile applications, or APIs — you confirm that you have read, understood, and agree to be bound by these Terms and our <Link to="/privacy" className="text-secondary hover:underline underline-offset-4">Privacy Policy</Link>.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    If you are using the platform on behalf of an organisation, you represent that you have the authority to bind that organisation to these Terms, and "you" refers to that organisation.
                  </p>
                </div>

                {/* 2. Definitions */}
                <div id="definitions" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={FileText} number="2" title="Definitions" />
                  <Table rows={[
                    ["Platform", "The BookMyCoaching website, mobile apps, APIs, and all associated services"],
                    ["Student", "A registered user who uses the platform to find and book teaching or tutoring services"],
                    ["Teacher", "A registered user who offers teaching, tutoring, or educational services through the platform"],
                    ["Offering", "A teaching product listed by a Teacher, including 1-on-1 sessions, group classes, or structured courses"],
                    ["Session", "A scheduled interaction between a Student and a Teacher facilitated through the platform"],
                    ["Content", "Any text, images, video, audio, profiles, reviews, or other material uploaded or shared via the platform"],
                    ["Fees", "Amounts charged for Sessions or Offerings, inclusive of platform service fees unless stated otherwise"],
                  ]} />
                </div>

                {/* 3. Eligibility */}
                <div id="eligibility" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={UserCheck} number="3" title="Eligibility" />
                  <p className="text-muted-foreground leading-relaxed mb-3">To use BookMyCoaching you must:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Be at least 13 years of age. Users aged 13–17 must have verifiable parental or guardian consent.</li>
                    <li>Have the legal capacity to enter into a binding contract in your jurisdiction.</li>
                    <li>Not have been previously suspended or permanently banned from the platform.</li>
                    <li>Not be located in a jurisdiction where the use of the platform is prohibited by applicable law.</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
                    Teachers must additionally be at least 18 years of age and legally permitted to offer educational services.
                  </p>
                </div>

                {/* 4. Accounts */}
                <div id="accounts" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={UserCheck} number="4" title="Accounts & Registration" />
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You must create an account to access core platform features. When registering you agree to:
                  </p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Provide accurate, current, and complete information during registration and keep it up to date.</li>
                    <li>Maintain the confidentiality of your login credentials and be fully responsible for all activity under your account.</li>
                    <li>Notify us immediately at <a href="mailto:support@bookmycoaching.com" className="text-secondary hover:underline underline-offset-4">support@bookmycoaching.com</a> if you suspect any unauthorised access.</li>
                    <li>Not share your account with any other person or allow others to access the platform through your credentials.</li>
                    <li>Not create more than one account per person without our prior written consent.</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4">
                    We reserve the right to suspend or terminate accounts that violate these requirements, contain false information, or are inactive for more than 24 consecutive months.
                  </p>
                </div>

                {/* 5. Platform Role */}
                <div id="platform-role" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Globe} number="5" title="Platform Role" />
                  <div className="p-5 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-5">
                    <p className="text-sm font-semibold text-foreground mb-1">Important — we are a marketplace</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      BookMyCoaching is a technology platform that connects Students and Teachers. We do <strong>not</strong> directly employ Teachers or provide teaching services. Teachers are independent contractors, not employees or agents of BookMyCoaching.
                    </p>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    While we verify Teacher credentials and facilitate safe interactions, we do not guarantee the accuracy of Teacher profiles, the quality of any Session, or outcomes of any teaching engagement. Students engage Teachers at their own discretion and responsibility.
                  </p>
                </div>

                {/* 6. Student Obligations */}
                <div id="student-obligations" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Star} number="6" title="Student Obligations" />
                  <p className="text-muted-foreground leading-relaxed mb-3">As a Student, you agree to:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Use the platform solely for legitimate personal learning and educational purposes.</li>
                    <li>Attend booked Sessions at the scheduled time, or cancel in accordance with our cancellation policy with reasonable notice.</li>
                    <li>Treat Teachers respectfully and professionally in all communications.</li>
                    <li>Not record Sessions without the explicit prior consent of the Teacher.</li>
                    <li>Not share, reproduce, or distribute Session content provided by a Teacher without their written permission.</li>
                    <li>Provide honest, fair, and constructive reviews based on your genuine experience.</li>
                    <li>Pay all applicable Fees on time and not initiate chargebacks or payment disputes without first attempting to resolve the issue through our support team.</li>
                  </ul>
                </div>

                {/* 7. Coach Obligations */}
                <div id="coach-obligations" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={UserCheck} number="7" title="Teacher Obligations" />
                  <p className="text-muted-foreground leading-relaxed mb-3">As a Teacher, you agree to:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Maintain an accurate and up-to-date profile, including qualifications, availability, and pricing.</li>
                    <li>Deliver Sessions at the agreed time, format, and quality described in your Offering.</li>
                    <li>Hold all qualifications, licences, and permissions required to offer your stated services in your jurisdiction.</li>
                    <li>Notify Students and BookMyCoaching promptly of any cancellation with at least 24 hours' notice where possible.</li>
                    <li>Treat Students respectfully and maintain professional boundaries at all times.</li>
                    <li>Not solicit Students to transact outside the platform in order to circumvent BookMyCoaching's fees.</li>
                    <li>Comply with all applicable laws including tax obligations in your country of residence. BookMyCoaching provides earnings reports but is not responsible for your tax filings.</li>
                    <li>Pass any re-verification requests initiated by BookMyCoaching within the specified timeframe.</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4 p-4 rounded-xl bg-muted/50 border border-border/40">
                    Violation of Teacher obligations may result in immediate suspension, removal of your Offering listings, withholding of pending payouts pending investigation, and permanent account termination.
                  </p>
                </div>

                {/* 8. Payments */}
                <div id="payments" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={CreditCard} number="8" title="Payments & Fees" />
                  <SubHeading>8.1 Student Payments</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    All prices displayed on the platform include applicable taxes unless stated otherwise. Payment is collected at the time of booking via our payment processor (Stripe). We accept major credit/debit cards, UPI, and net banking.
                  </p>
                  <SubHeading>8.2 Platform Service Fee</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    Book My Coaching is committed to making quality teaching accessible to everyone. As part of our launch offer, the platform is completely free to use for the first 6 months from the date of registration — no hidden charges, no credit card required. This includes full access to teacher discovery, session booking, messaging, and scheduling tools. After the 6-month free period, a nominal platform service fee may apply to continued use of premium features. We will notify all users well in advance before any charges take effect, giving you ample time to review the updated terms and make an informed decision.
                  </p>
                  <SubHeading>8.3 Teacher Payouts</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Book My Coaching does not facilitate, process, or manage any financial transactions between teachers and students. All payments and payouts are handled directly between the student and the teacher through their mutually agreed payment method. The platform bears no responsibility for payment disputes, delayed transfers, incorrect amounts, or any financial discrepancies that may arise in the course of these transactions. Teachers and students are encouraged to establish clear payment terms, preferred methods, and schedules prior to commencing any teaching engagement. Book My Coaching acts solely as a discovery and booking platform and is not a party to any financial agreement between its users.
                  </p>
                  {/* TODO(legal-review): new subsections added by teacher-subscription feature, needs Legal sign-off before merge */}
                  <SubHeading>8.4 Ad-Boost Forfeit Policy</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    If a Teacher subscribes to a paid subscription tier while a purchased ad-boost is still active, the remaining ad-boost is forfeited immediately with no refund. The paid subscription tier will take precedence, and the Teacher will receive the benefits of their subscription tier instead. Teachers should carefully consider the timing of their subscription purchase to avoid unintended forfeit of active ad-boosts.
                  </p>
                  <SubHeading>8.5 No Prorated Refunds on Subscription Cancellation</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Cancelling a subscription does not entitle the Teacher to any prorated or partial refund of the subscription amount. The Teacher retains full access to all subscription tier benefits until the end of the current billing period, after which the subscription lapses and the tier benefits cease. No refund will be issued for any portion of the billing period following cancellation.
                  </p>
                </div>

                {/* 9. Cancellations & Refunds */}
                <div id="cancellations" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={RefreshCw} number="9" title="Cancellations & Refunds" />
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Cancellations and refunds are managed directly between the client and the teacher. Book My Coaching serves solely as a platform to connect clients with teachers and does not assume any responsibility for cancellation requests, refund disputes, or outcomes arising from session changes. Any concerns regarding cancellations or refunds should be addressed directly with your teacher. We encourage all parties to review and agree on individual cancellation terms before booking a session.
                  </p>
                </div>

                {/* 10. User Content */}
                <div id="content" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={FileText} number="10" title="User Content" />
                  <p className="text-muted-foreground leading-relaxed mb-3 text-sm">
                    You retain ownership of any Content you upload to the platform. By uploading Content, you grant BookMyCoaching a non-exclusive, royalty-free, worldwide licence to use, store, display, and reproduce that Content solely for the purposes of operating and improving the platform.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3 text-sm">You are solely responsible for your Content and represent that:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>You own or have the necessary rights to the Content you upload.</li>
                    <li>Your Content does not infringe any third-party intellectual property, privacy, or other rights.</li>
                    <li>Your Content does not contain false, misleading, defamatory, obscene, or illegal material.</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4">
                    We reserve the right to remove any Content that violates these Terms without prior notice.
                  </p>
                </div>

                {/* 11. Prohibited Conduct */}
                <div id="prohibited" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Ban} number="11" title="Prohibited Conduct" />
                  <p className="text-muted-foreground leading-relaxed mb-3 text-sm">The following actions are strictly prohibited and may result in immediate account termination:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Impersonating another person, organisation, or teacher on the platform</li>
                    <li>Creating fake reviews or manipulating the rating system</li>
                    <li>Soliciting personal contact details from other users to conduct off-platform transactions</li>
                    <li>Scraping, crawling, or using automated tools to extract platform data</li>
                    <li>Uploading malware, viruses, or any code designed to harm the platform or its users</li>
                    <li>Engaging in harassment, abuse, discrimination, or hate speech of any kind</li>
                    <li>Using the platform to facilitate illegal activity of any kind</li>
                    <li>Attempting to reverse-engineer, decompile, or gain unauthorised access to our systems</li>
                    <li>Facilitating or encouraging any of the above by third parties</li>
                  </ul>
                </div>

                {/* 12. IP */}
                <div id="ip" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Lock} number="12" title="Intellectual Property" />
                  <p className="text-muted-foreground leading-relaxed mb-3 text-sm">
                    All intellectual property in the BookMyCoaching platform — including its software, design, logos, trademarks, algorithms, and original Content — is owned by or licensed to BookMyCoaching Pvt. Ltd. Nothing in these Terms transfers any IP ownership to you.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    You are granted a limited, non-exclusive, non-transferable, revocable licence to use the platform for its intended purpose only. This licence is automatically revoked upon termination of your account.
                  </p>
                </div>

                {/* 13. Third-party */}
                <div id="third-party" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Globe} number="13" title="Third-Party Services" />
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    The platform may contain links to or integrations with third-party services (e.g. Zoom, Google Meet, payment processors). These services have their own terms and privacy policies, and BookMyCoaching has no control over and accepts no responsibility for their content, practices, or availability. Your use of third-party services is at your own risk.
                  </p>
                </div>

                {/* 14. Disclaimers */}
                <div id="disclaimers" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={ShieldAlert} number="14" title="Disclaimers" />
                  <div className="p-5 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">The platform is provided "as is" and "as available"</strong> without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
                    </p>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">We do not warrant that:</p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 mt-2 leading-relaxed">
                    <li>The platform will be uninterrupted, error-free, or free from viruses or other harmful components.</li>
                    <li>Any Teacher profile information, credentials, or representations are accurate or verified to any particular standard.</li>
                    <li>Any specific educational outcomes or results will be achieved through use of the platform.</li>
                  </ul>
                </div>

                {/* 15. Liability */}
                <div id="liability" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Scale} number="15" title="Limitation of Liability" />
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    To the maximum extent permitted by applicable law, BookMyCoaching shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, or other intangible losses arising from:
                  </p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Your use of or inability to use the platform</li>
                    <li>Any conduct or content of any Teacher or Student on the platform</li>
                    <li>Unauthorised access to or alteration of your data</li>
                    <li>Any third-party services or content linked to from the platform</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4 p-4 rounded-xl bg-muted/50 border border-border/40">
                    In jurisdictions that do not allow exclusion of certain warranties or limitation of liability, our liability is limited to the maximum extent permitted by law. In any event, our aggregate liability to you shall not exceed the total Fees paid by you to BookMyCoaching in the <strong className="text-foreground">12 months preceding the claim</strong>.
                  </p>
                </div>

                {/* 16. Indemnification */}
                <div id="indemnification" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={ShieldAlert} number="16" title="Indemnification" />
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    You agree to defend, indemnify, and hold harmless BookMyCoaching, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with your access to or use of the platform, your violation of these Terms, your Content, or your violation of any third-party rights.
                  </p>
                </div>

                {/* 17. Termination */}
                <div id="termination" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Ban} number="17" title="Termination" />
                  <SubHeading>17.1 Termination by You</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    You may close your account at any time from your Account Settings. Deletion requests are processed within 30 days. Certain data may be retained longer as required by law (see our <Link to="/privacy" className="text-secondary hover:underline underline-offset-4">Privacy Policy</Link>). Pending payouts due to you as a Teacher will be processed before account closure.
                  </p>
                  <SubHeading>17.2 Termination by BookMyCoaching</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    We may suspend or permanently terminate your account at our discretion if we reasonably believe you have violated these Terms, engaged in fraudulent activity, caused harm to other users, or for any other legitimate business reason. Where possible, we will provide advance notice.
                  </p>
                  <SubHeading>17.3 Effect of Termination</SubHeading>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Upon termination, your licence to use the platform ceases immediately. Provisions that by their nature should survive termination (including ownership, disclaimers, limitation of liability, and governing law) will continue to apply.
                  </p>
                </div>

                {/* 18. Governing Law */}
                <div id="governing-law" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={Gavel} number="18" title="Governing Law & Dispute Resolution" />
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    These Terms are governed by the laws of India, without regard to its conflict-of-law provisions. Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of Noida, Uttar Pradesh, India.
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    Before filing any legal claim, you agree to first contact us at <a href="mailto:legal@bookmycoaching.com" className="text-secondary hover:underline underline-offset-4">legal@bookmycoaching.com</a> and attempt to resolve the dispute informally for a period of 30 days.
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    For consumer disputes, you may also refer the matter to the relevant Consumer Disputes Redressal Forum under the Consumer Protection Act, 2019 (India).
                  </p>
                </div>

                {/* 19. Changes */}
                <div id="changes" className="mb-12 scroll-mt-24">
                  <SectionHeading icon={RefreshCw} number="19" title="Changes to Terms" />
                  <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                    We may revise these Terms from time to time. When we make material changes, we will:
                  </p>
                  <ul className="list-disc pl-5 text-muted-foreground text-sm space-y-2 leading-relaxed">
                    <li>Post the updated Terms with a revised "Last updated" date</li>
                    <li>Send an email notification to your registered address at least 14 days before changes take effect</li>
                    <li>Display a notice on the platform on your next login</li>
                  </ul>
                  <p className="text-muted-foreground text-sm mt-4">
                    Continued use after the effective date constitutes acceptance. If you do not agree to the revised Terms, you must stop using the platform and may delete your account before the effective date.
                  </p>
                </div>

                {/* 20. Contact */}
                <div id="contact" className="mb-4 scroll-mt-24">
                  <SectionHeading icon={Mail} number="20" title="Contact Us" />
                  <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                    For questions about these Terms, to report a violation, or for any legal enquiries:
                  </p>
                  <div className="rounded-2xl border border-border/40 bg-card p-6 grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">BookMyCoaching Pvt. Ltd.</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Urbtech Trade Centre<br />
                        Sector 132, Noida 201304<br />
                        India
                      </p>
                    </div>
                    <div className="flex flex-col gap-3">
                      <a href="mailto:support@bookmycoaching.com" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline underline-offset-4">
                        <Mail className="w-4 h-4" />
                        support@bookmycoaching.com
                      </a>
                      <Link to="/contact" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline underline-offset-4">
                        <ArrowRight className="w-4 h-4" />
                        Use our contact form
                      </Link>
                    </div>
                  </div>
                </div>

              </article>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ── Sub-components ── */
function SectionHeading({ icon: Icon, number, title }: { icon: React.ElementType; number: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <h2 className="font-sans text-xl sm:text-2xl text-foreground">
        <span className="text-muted-foreground font-normal text-base mr-2">{number}.</span>
        {title}
      </h2>
    </div>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide mt-6 mb-3">{children}</h3>;
}

function Table({ rows }: { rows: [string, string][] }) {
  return (
    <div className="rounded-xl border border-border/40 overflow-hidden mb-4">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, value], i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-muted/30" : "bg-background"}>
              <td className="px-4 py-3 font-medium text-foreground w-1/3 align-top border-r border-border/40">{label}</td>
              <td className="px-4 py-3 text-muted-foreground leading-relaxed">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
